/* Storefront behaviour for the Minties sections: header menus, tabs, sliders, newsletter trigger. */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function visibleFocusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
  }

  /* Desktop product dropdown: opens on hover or keyboard focus, closes on Escape or when focus leaves the header. */
  function initHeaderDropdown(header) {
    var trigger = header.querySelector('[data-dropdown-trigger]');
    var dropdown = header.querySelector('[data-dropdown]');
    if (!trigger || !dropdown) return;
    var dismissed = false;

    function open() {
      dropdown.classList.add('active');
      trigger.setAttribute('aria-expanded', 'true');
    }
    function close() {
      dropdown.classList.remove('active');
      trigger.setAttribute('aria-expanded', 'false');
    }

    trigger.addEventListener('mouseenter', open);
    trigger.addEventListener('focus', function () {
      if (!dismissed) open();
      dismissed = false;
    });
    header.querySelectorAll('.header-link').forEach(function (link) {
      link.addEventListener('mouseenter', close);
    });
    header.addEventListener('mouseleave', close);
    header.addEventListener('focusout', function (event) {
      if (!header.contains(event.relatedTarget)) close();
    });
    header.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && dropdown.classList.contains('active')) {
        close();
        if (document.activeElement !== trigger) {
          dismissed = true;
          trigger.focus();
        }
      } else if (event.key === 'ArrowDown' && document.activeElement === trigger) {
        var first = dropdown.querySelector('a[href]');
        if (first) {
          event.preventDefault();
          open();
          first.focus();
        }
      }
    });
  }

  /* Mobile menu: modal drawer with one level of submenus. */
  function initMobileMenu(header) {
    var menu = header.querySelector('[data-mobile-menu]');
    var openButton = header.querySelector('[data-menu-open]');
    if (!menu || !openButton) return;
    var backButton = menu.querySelector('[data-menu-back]');
    var parents = menu.querySelector('[data-menu-parents]');
    var subMenus = menu.querySelectorAll('.mobile-sub-links');
    var subButtons = menu.querySelectorAll('[data-menu-sub]');
    var lastSubButton = null;

    function showParents() {
      backButton.classList.remove('active');
      backButton.disabled = true;
      parents.classList.add('active');
      subMenus.forEach(function (list) {
        list.classList.remove('active');
      });
      subButtons.forEach(function (button) {
        button.setAttribute('aria-expanded', 'false');
      });
    }
    function open() {
      menu.inert = false;
      menu.classList.add('active');
      document.body.classList.add('disable-scroll');
      openButton.setAttribute('aria-expanded', 'true');
      var first = visibleFocusables(menu)[0];
      if (first) first.focus();
    }
    function close() {
      // The closed drawer is only moved off-screen, so inert keeps it out of the tab order.
      menu.inert = true;
      menu.classList.remove('active');
      document.body.classList.remove('disable-scroll');
      openButton.setAttribute('aria-expanded', 'false');
      showParents();
      openButton.focus();
    }

    openButton.addEventListener('click', open);
    menu.querySelectorAll('[data-menu-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    subButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        var list = document.getElementById(button.getAttribute('data-menu-sub'));
        if (!list) return;
        lastSubButton = button;
        backButton.classList.add('active');
        backButton.disabled = false;
        parents.classList.remove('active');
        list.classList.add('active');
        button.setAttribute('aria-expanded', 'true');
        var first = list.querySelector('a[href]');
        if (first) first.focus();
      });
    });
    backButton.addEventListener('click', function () {
      showParents();
      if (lastSubButton) lastSubButton.focus();
    });
    menu.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      var items = visibleFocusables(menu);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* Tabs: any [data-tabs] wrapper holding role="tab" elements that point at panels with aria-controls. */
  function initTabs(root) {
    if (root.tabsReady) return;
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;
    root.tabsReady = true;

    function select(tab, moveFocus) {
      tabs.forEach(function (item) {
        var selected = item === tab;
        var panel = document.getElementById(item.getAttribute('aria-controls'));
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        item.tabIndex = selected ? 0 : -1;
        item.classList.toggle('active', selected);
        if (item.parentElement.getAttribute('role') === 'presentation') {
          item.parentElement.classList.toggle('active', selected);
        }
        if (panel) {
          panel.classList.toggle('active', selected);
          panel.classList.toggle('tab-content--active', selected);
          panel.inert = !selected;
        }
      });
      if (moveFocus) tab.focus();
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function (event) {
        event.preventDefault();
        select(tab, false);
      });
      tab.addEventListener('keydown', function (event) {
        var target = null;
        if (event.key === 'ArrowRight') target = tabs[(index + 1) % tabs.length];
        if (event.key === 'ArrowLeft') target = tabs[(index - 1 + tabs.length) % tabs.length];
        if (event.key === 'Home') target = tabs[0];
        if (event.key === 'End') target = tabs[tabs.length - 1];
        if (event.key === ' ') target = tab;
        if (!target) return;
        event.preventDefault();
        select(target, true);
      });
    });
  }

  /* Sliders: Swiper options come from the element's data-slider JSON; arrows and dots are found in the nearest [data-slider-root]. */
  function initSliders(scope) {
    if (typeof Swiper === 'undefined') return;
    scope.querySelectorAll('[data-slider]').forEach(function (element) {
      if (element.swiper) return;
      var root = element.closest('[data-slider-root]') || element.parentElement;
      var options = {};
      try {
        options = JSON.parse(element.getAttribute('data-slider') || '{}');
      } catch (error) {
        options = {};
      }
      var next = root.querySelector('.swipe-button-next, .swiper-btn-next');
      var prev = root.querySelector('.swipe-button-prev, .swiper-btn-prev');
      var pagination = root.querySelector('.swiper-pagination');
      if (next && prev) options.navigation = { nextEl: next, prevEl: prev };
      if (pagination) {
        options.pagination = {
          el: pagination,
          clickable: true,
          renderBullet: function (index, className) {
            return '<div class="' + className + '"></div>';
          },
        };
      }
      new Swiper(element, options);
    });
  }

  function init(scope) {
    scope.querySelectorAll('[data-site-header]').forEach(function (header) {
      if (header.headerReady) return;
      header.headerReady = true;
      initHeaderDropdown(header);
      initMobileMenu(header);
    });
    scope.querySelectorAll('[data-tabs]').forEach(initTabs);
    if (scope.matches && scope.matches('[data-tabs]')) initTabs(scope);
    initSliders(scope);
  }

  /* Footer newsletter button opens the Klaviyo popup form. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-klaviyo-form]');
    if (!trigger) return;
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', trigger.getAttribute('data-klaviyo-form')]);
  });

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
    if (window.ShopifyScrollFX) window.ShopifyScrollFX.init();
  });

  /* Theme editor: sections are re-rendered in place, so wire up the new markup. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
